import { useEffect, useRef, useState } from 'react'
import {
  CustomTable,
  CustomTableRef,
  stringFilterTemplate,
  DateFilterTemplate,
  ETypes,
} from 'medere-ui-framework'
import {
  getAllStudiesInform,
  getStudyTypes,
  cancelStudyInform,
  exportStudiesInform,
} from '../../../../services/StudiesInformController'
import CancelInformModal from './CancelInformModal'
import { StudiesInformListItemDTO } from '../../../../dto/StudiesInformListItemDTO'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { Dropdown } from 'primereact/dropdown'
import { ColumnFilterElementTemplateOptions } from 'primereact/column'

import { FilterMatchMode } from 'primereact/api'
import StudiesInformCrudPanel from '../../StudiesInformCrudPanel'
import { formatDateWithTime } from '../../helpers'

function getDefaultDateFilters() {
  const today = new Date()
  const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())
  return {
    servicedatetime: { value: { min: oneMonthAgo, max: null }, matchMode: FilterMatchMode.CUSTOM },
  }
}

function PendingCard({ entity, btn1, btn2 }: { entity: StudiesInformListItemDTO; btn1?: any; btn2?: any }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem' }}>
      <div>
        <strong>{entity.patientFullName}</strong>
        <p style={{ margin: 0 }}>{entity.studyTypeLabel} — {entity.requestedDate}</p>
        <p style={{ margin: 0, color: '#888' }}>{entity.professionalFullName} | {entity.location}</p>
      </div>
      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
        {btn1 && <Button className={btn1.className} icon={"pi " + btn1.icon} onClick={btn1.onClick} />}
        {btn2 && <Button className={btn2.className} icon={"pi " + btn2.icon} onClick={btn2.onClick} />}
      </div>
    </div>
  )
}

export default function PendingView() {
  const tableRef = useRef<CustomTableRef>(null)
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null)
  const [selectedEntityMode, setSelectedEntityMode] = useState<'view' | 'edit'>('view')
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  const [typeOptions, setTypeOptions] = useState<Array<{ label: string; value: string }>>([])
  const defaultFilters = useState(getDefaultDateFilters)[0]
  const [cancelModalVisible, setCancelModalVisible] = useState(false)
  const [cancelTargetId, setCancelTargetId] = useState<number | null>(null)
  const [cancelLoading, setCancelLoading] = useState(false)
  
  useEffect(() => {
    getStudyTypes().then(setTypeOptions).catch(() => setTypeOptions([]))
  }, [])

  function openView(item: StudiesInformListItemDTO) {
    setSelectedEntityMode('edit')
    setSelectedEntityId(item.studyDiagnosticId)
  }

  function openManualInform() {
    setSelectedEntityMode('edit')
    setSelectedEntityId(0)
  }

  function cancelInform(item: StudiesInformListItemDTO) {
    setCancelTargetId(item.studyDiagnosticId)
    setCancelModalVisible(true)
  }

  function handleCancelConfirm(reason: string) {
    if (cancelTargetId == null) return
    setCancelLoading(true)
    cancelStudyInform(cancelTargetId, reason)
      .then(() => {
        setCancelModalVisible(false)
        setCancelTargetId(null)
        tableRef.current?.refreshTable?.()
      })
      .finally(() => setCancelLoading(false))
  }

  if (selectedEntityId != null) {
    return (
      <StudiesInformCrudPanel
        StudyInformId={selectedEntityId}
        setStudyInformId={setSelectedEntityId}
        mode={selectedEntityMode}
        setMode={setSelectedEntityMode}
        onSaved={() => tableRef.current?.refreshTable?.()}
      />
    )
  }

  return (
    <>
    <CancelInformModal
      visible={cancelModalVisible}
      onHide={() => { setCancelModalVisible(false); setCancelTargetId(null) }}
      onConfirm={handleCancelConfirm}
      loading={cancelLoading}
    />
    <div className='viewContainer studies-inform-view'>
      <div className='studies-inform-actions-row'>
        <Button
          type='button'
          label='Crear informe'
          icon='pi pi-plus'
          className='studies-inform-crud-primary-btn'
          onClick={openManualInform}
        />
      </div>
      <CustomTable
        idTable='StudiesInformTable'
        className='studies-inform-table'
        ref={tableRef}
        getItems={getAllStudiesInform}
        params={{
          typeFilter,
        }}
        defaultFilters={defaultFilters}
        defaultsOverwrite
        filtersType={[{ field: 'servicedatetime', type: ETypes.localDateTime }]}
        resizableColumns
        dynamicBehaviour
        CardTemplate={PendingCard}
        cardKeyName='studyDiagnosticId'
        filterRow
        hideFiltersButton
        headerClassName='studies-inform-header'
        refreshButton
        exportItems={[{ label: 'Excel', icon: 'pi-file-excel', onExport: exportStudiesInform }]}
        onClearFilters={() => { setTypeFilter(null); }}
      >
        <Column
          field='patientFullName'
          className='column'
          header='Paciente'
          filter
          filterElement={stringFilterTemplate}
          body={(row: StudiesInformListItemDTO) => (
            <div>
              <div>{row.patientFullName}</div>
              <div style={{ fontSize: '0.8em', color: '#888' }}>{row.patientFullDoc}</div>
            </div>
          )}
        />
        <Column
          field='servicedatetime'
          className='column'
          header='Fecha y hora'
          filter
          filterElement={DateFilterTemplate}
          body={(row: StudiesInformListItemDTO) => formatDateWithTime(row.serviceDate)}
          style={{ minWidth: '13rem' }}
        />
        <Column
          field='studyTypeLabel'
          className='column'
          header='Estudio'
          filter
          filterElement={(filter: ColumnFilterElementTemplateOptions) => (
            <Dropdown
              options={typeOptions}
              optionLabel='label'
              optionValue='value'
              value={typeFilter}
              showClear
              className='w-full'
              style={{ height: '2.25rem', marginBottom: '0.28rem' }}
              onChange={(e) => {
                const newValue = (e.value ?? null) as string | null
                setTypeFilter(newValue)
                filter.filterCallback(newValue)
              }}
            />
          )}
          style={{ minWidth: '10rem' }}
        />
        <Column 
          field='professionalFullName' 
          className='column' 
          header='Médico solicitante' 
          filter 
          filterElement={stringFilterTemplate} 
        />
        <Column
          field='location'
          className='column'
          header='Locación'
          filter
          filterElement={stringFilterTemplate}
        />
        <Column
          className='column'
          filter
          filterElement={() => (
            <Button
              icon='pi pi-eraser'
              className='buttonControl'
              tooltip='Limpiar filtros'
              tooltipOptions={{ position: 'top' }}
              onClick={() => {
                tableRef.current?.internalClearFilters?.()
                setTypeFilter(null)
              }}
            />
          )}
          body={(row: StudiesInformListItemDTO) => (
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <Button className='buttonTable' icon='pi pi-file' onClick={() => openView(row)} />
              <Button className='buttonTableInv' icon='pi pi-times' onClick={() => cancelInform(row)} />
            </div>
          )}
        />
      </CustomTable>
    </div>
    </>
  )
}
